














































































































































































































































import Component, { mixins } from 'vue-class-component';
import { currencyFormat, loadView, loadWidget } from '@/utils/helpers';
import { namespace } from 'vuex-class';
import { IAutocreditLedger, IAutocreditStatusTotal, IOrganizations, IPaginate, IStore, IUser } from '@/types/types';
import timestamp from '@/filters/timestamp';
import { Watch } from 'vue-property-decorator';
import WidgetMixins from '@/mixins/WidgetMixins';
import DialogMixins from '@/mixins/DialogMixins';
import qs from 'qs';

const autocreditModule = namespace('autocredit');
const organizationModule = namespace('organizations');
const authModule = namespace('auth');

@Component({
  name: 'AutocreditProcessed',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    AutocreditResolveDialog: loadView('pages/products/dialogs/AutocreditResolveDialog'),
  },
  filters: {
    currencyFormat,
    timestamp,
  },
})
export default class AutocreditProcessed extends mixins(WidgetMixins, DialogMixins) {
  @authModule.Getter('getUserDetails') details!: IUser & IStore;
  @autocreditModule.Getter('getLedgers') ledgers!: IAutocreditLedger[];
  @autocreditModule.Getter('getLedgersPagination') ledgersPagination!: IPaginate;
  @autocreditModule.Getter('getLedgerTotals') ledgerTotals!: IAutocreditStatusTotal[];
  @organizationModule.Getter('getAllOrganizations') organizations!: IOrganizations[];

  options: any = {};
  query: string = '';
  businessDateMenu: boolean = false;
  businessDates: string[] = [];
  organizationId: string | null = null;
  search: string = '';
  // Empty = every processed status (the API excludes REVIEW_PENDING by default).
  status: string | null = null;
  expanded: IAutocreditLedger[] = [];

  statusOptions: Array<{ text: string; value: string | null }> = [
    { text: 'ALL PROCESSED', value: null },
    { text: 'POSTED', value: 'POSTED' },
    { text: 'PENDING', value: 'PENDING' },
    { text: 'PROCESSING', value: 'PROCESSING' },
    { text: 'FAILED', value: 'FAILED' },
  ];

  headers: Array<{ text: string; value: string; align?: string; sortable?: boolean }> = [
    { text: 'Business Date', value: 'businessDate' },
    { text: 'Organization', value: 'organizationName' },
    { text: 'MSISDN', value: 'msisdn' },
    { text: 'Txns', value: 'transactionCount', align: 'end' },
    { text: 'Amount', value: 'amount', align: 'end' },
    { text: 'Status', value: 'status' },
    { text: 'Authorized By', value: 'reviewAuthorizedBy' },
    { text: 'Last Updated', value: 'updatedAt' },
    { text: '', value: 'actions', sortable: false },
    { text: '', value: 'data-table-expand' },
  ];

  get businessDatesLabel(): string {
    const dates = [...this.businessDates].sort();
    if (dates.length === 0) return '';
    if (dates.length <= 3) return dates.join(', ');
    return `${dates[0]} — ${dates[dates.length - 1]} (${dates.length} days)`;
  }

  timestampOf(value: string): string {
    return timestamp(value, 'DD/MM/YYYY HH:mm');
  }

  resolvingLedger: IAutocreditLedger | null = null;

  openResolveDialog(ledger: IAutocreditLedger): void {
    this.resolvingLedger = ledger;
    this.open('resolve');
  }

  handleResolve(payload: {
    action: 'retry' | 'new-reference' | 'mark-posted';
    ledgerId: string;
    reason?: string;
    downstreamReference?: string;
    confirmedNotPaid?: boolean;
  }): void {
    this.$store.dispatch('autocredit/resolveLedger', {
      ...payload,
      adminId: this.details?.id ?? this.details?.email,
      query: this.query,
    });
  }

  statusClass(status: string): string {
    const classes: { [key: string]: string } = {
      REVIEW_PENDING: 'ka-status--warning',
      PENDING: 'ka-status--info',
      PROCESSING: 'ka-status--info',
      POSTED: 'ka-status--success',
      FAILED: 'ka-status--error',
    };
    return classes[status] || 'ka-status--info';
  }

  statIconClass(status: string): string {
    const classes: { [key: string]: string } = {
      POSTED: 'ka-stat__icon--success',
      FAILED: 'ka-stat__icon--error',
      PENDING: 'ka-stat__icon--info',
      PROCESSING: 'ka-stat__icon--info',
    };
    return classes[status] || 'ka-stat__icon--info';
  }

  statIcon(status: string): string {
    const icons: { [key: string]: string } = {
      POSTED: 'task_alt',
      FAILED: 'error_outline',
      PENDING: 'hourglass_top',
      PROCESSING: 'autorenew',
    };
    return icons[status] || 'receipt_long';
  }

  buildQuery(page: number = 1, limit: number = 50): string {
    return qs.stringify(
      {
        page,
        limit,
        organizationId: this.organizationId,
        ...(this.status && { statuses: this.status }),
        ...(this.businessDates.length && { businessDates: this.businessDates.join(',') }),
        ...(this.search && { search: this.search }),
      },
      { skipNulls: true, addQueryPrefix: true },
    );
  }

  fetchLedgers(): void {
    this.$store.dispatch('autocredit/fetchLedgers', this.query);
  }

  refreshFromFilters(): void {
    this.query = this.buildQuery(1, this.options?.itemsPerPage || 50);
    this.fetchLedgers();
  }

  applySearch(): void {
    this.refreshFromFilters();
  }

  clearSearch(): void {
    this.search = '';
    this.refreshFromFilters();
  }

  @Watch('organizationId')
  onOrganizationIdChange(): void {
    this.refreshFromFilters();
  }

  @Watch('status')
  onStatusChange(): void {
    this.refreshFromFilters();
  }

  @Watch('businessDates')
  onBusinessDatesChange(): void {
    this.refreshFromFilters();
  }

  @Watch('options', { deep: true })
  onOptionsChange(paginate: IPaginate): void {
    const query = this.buildQuery(paginate.page, paginate.itemsPerPage);
    if (query === this.query) return;
    this.query = query;
    this.fetchLedgers();
  }

  created(): void {
    this.$store.dispatch('auth/me');
    this.$store.dispatch('organizations/list', false);
    this.query = this.buildQuery();
    this.fetchLedgers();
  }
}
